import "@mavenmm_dev/dev-library/ui/styles.css";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import { FeedbackProvider, FeedbackLauncher, FeedbackWidget, type FeedbackTransport, type UiFeedbackConfig } from "@mavenmm_dev/dev-library/ui";
import type { ReactNode } from "react";

// Screenshots (S3) and video (Vimeo) light up when their env is configured AND the
// build flags are set: VITE_FEEDBACK_RICHTEXT=true, VITE_FEEDBACK_VIDEO=true.
// Until then the widget is text-only and the image/video endpoints are never called.
const RICHTEXT = import.meta.env.VITE_FEEDBACK_RICHTEXT === "true";
const VIDEO = import.meta.env.VITE_FEEDBACK_VIDEO === "true";

export function FeedbackRoot({ children }: { children: ReactNode }) {
  const { getAccessToken } = useAuthContext();

  const postJson = async (path: string, payload: unknown): Promise<Response> => {
    const token = getAccessToken();
    return fetch(path, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
    });
  };

  const transport: FeedbackTransport = {
    async submitText(payload) {
      return (await postJson("/api/feedback", payload)).json();
    },
    async uploadImage(file) {
      const token = getAccessToken();
      const res = await fetch("/api/feedback-image", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": (file as File).type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: file,
      });
      if (!res.ok) throw new Error(`Screenshot upload failed (${res.status})`);
      return res.json();
    },
    async createVideoTarget(sizeBytes, subject) {
      const res = await postJson("/api/feedback-video-target", { sizeBytes, subject });
      if (!res.ok) throw new Error(`Couldn't start the video upload (${res.status})`);
      return res.json();
    },
    async submitVideo(payload) {
      return (await postJson("/api/feedback-video", payload)).json();
    },
  };

  const config: UiFeedbackConfig = { transport, enableRichText: RICHTEXT, enableVideo: VIDEO };

  return (
    <FeedbackProvider config={config}>
      {children}
      <FeedbackWidget />
    </FeedbackProvider>
  );
}

export { FeedbackLauncher };
